<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <import-page-banner
                title="Last Mile Reference"
                sample-csv-path="/updateLastMileReferenceByImportSample.csv"
            />

            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6"> Bulk Update Direct Order Last Mile by Import </v-col>
                        <v-col cols="12" sm="12" md="6">
                            <common-list-reference
                                :btn-color="courierReferenceProps.btnColor"
                                :btn-text="courierReferenceProps.btnText"
                                :title="courierReferenceProps.title"
                                :description="courierReferenceProps.description"
                                :item-code-header="courierReferenceProps.itemCodeHeader"
                                :item-desc-header="courierReferenceProps.itemDescHeader"
                                :reference-list="courierReferenceProps.referenceList"
                            ></common-list-reference>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <p><b>Only accept csv file.</b></p>
                    <p>1. Choose your file</p>
                    <p>2. SELECT THE CORRECT MAPPING OF Field and Column</p>
                    <p>3. IMPORT</p>
                    <!-- New CSV Importer Component -->
                    <csv-importer
                        :field-definitions="data.fields"
                        :preview-headers="data.previewHeaders"
                        @data-loaded="onCsvDataLoaded"
                        @data-cleared="onCsvDataCleared"
                    />
                </v-card-text>
            </v-card>
            <br />
            <v-card v-if="data.mappedCsv && data.mappedCsv.length > 0">
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="12">
                            <v-btn
                                class="mr-2"
                                color="primary"
                                :loading="loading"
                                :disabled="loading || invalidFile"
                                @click="onImportClick"
                            >
                                <v-icon>mdi-cloud-upload</v-icon>
                                Import
                            </v-btn>
                            <v-btn class="mr-2" :to="pathListDirectOrder"> Back to list </v-btn>
                            <span v-if="importDone">{{ importDoneMsg }} </span>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in data.headers" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, idx) in transformMappedCsv(data.mappedCsv)"
                                :key="`row${idx}`"
                                :class="invalidRowClass(item)"
                            >
                                <td v-for="header in data.headers" :key="header.value">
                                    {{ item[header.value] }}
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { DirectOrderLabelTextHelper } from '../../utils/labelTextHelper/DirectOrderLabelTextHelper';
import * as _ from 'lodash';
import { PathDefinition } from '../../utils/PathDefinition';
import { ImportTableLabelTextHelper } from '../../utils/labelTextHelper/ImportTableLabelTextHelper';
import { SharedMsgHelper } from '../../utils/SharedMsgHelper';
import { ValidationHelper } from '../../utils/ValidationHelper';
import CommonListReference from '../../components/CommonListReference.vue';
import { ImportHeaderHelper } from '../../utils/ImportHeaderHelper';
import { MasterDataApiCaller } from '../../utils/MasterDataApiCaller';
import { DirectOrderHeaderHelper } from '../../utils/DirectOrderHeaderHelper';
import { Formatter } from '../../utils/Formatter';
import CsvImporter from '../../components/CsvImporter.vue';
import ImportPageBanner from '../../components/ImportPageBanner.vue';
import { ImportValidationHelper } from '../../utils/ImportValidationHelper';
import { UpdateLastMileReferenceImportValidation } from '../../config/importValidationConfigs';

export default defineComponent({
    name: 'ImportUpdateLastMileReference',

    components: {
        CommonListReference,
        CsvImporter,
        ImportPageBanner
    },

    setup() {
        const data = reactive({
            mappedCsv: null,
            fields: DirectOrderHeaderHelper.getImportDirectOrderFields(),
            previewHeaders: DirectOrderHeaderHelper.getImportDirectOrderHeaders(),
            headers: [...ImportHeaderHelper.getHeaders(), ...DirectOrderHeaderHelper.getImportDirectOrderHeaders()]
        });
        return {
            data
        };
    },
    data() {
        return {
            loading: false,
            invalidFile: false,
            importDone: false,
            importDoneMsg: SharedMsgHelper.getImportDoneMsg,
            pathListDirectOrder: PathDefinition.listDirectOrder,
            formLabelText: DirectOrderLabelTextHelper.getLabelText,
            courierCodeList: [],
            courierReferenceProps: {
                btnColor: 'secondary',
                btnText: 'Service List',
                title: 'Service List For Reference',
                description: '',
                itemCodeHeader: '',
                itemDescHeader: 'Service',
                referenceList: []
            }
        };
    },
    async created() {
        this.courierCodeList = await MasterDataApiCaller.getMasterCourierList();
        this.courierReferenceProps.referenceList = _.map(this.courierCodeList, (code) => {
            return {
                code: '',
                desc: code
            };
        });
    },
    methods: {
        onCsvDataLoaded(mappedData) {
            // Receive data from CSV importer component
            this.data.mappedCsv = mappedData;
        },
        onCsvDataCleared() {
            // Clear data when file is removed
            this.data.mappedCsv = null;
            this.importDone = false;
            this.invalidFile = false;
        },
        transformMappedCsv(mappedCsv) {
            const importHeaderValues = this.data.headers.map((header) => header.value);
            const lowercaseFields = ['directOrderCourierCode'];
            
            // First apply validation
            let validatedRows = ImportValidationHelper.validateRows(mappedCsv, UpdateLastMileReferenceImportValidation, importHeaderValues);
            
            // Then apply lowercase formatting and courier code validation
            return _.map(validatedRows, (item) => {
                lowercaseFields.forEach((field) => {
                    if (item[field] && item[field] !== SharedMsgHelper.getMissingRequiredErrMsg) {
                        item[field] = Formatter.lowerCaseFormatter(item[field]);
                    }
                });
                
                // Validate courier code against the list
                if (
                    item.directOrderCourierCode &&
                    item.directOrderCourierCode !== SharedMsgHelper.getMissingRequiredErrMsg &&
                    !ValidationHelper.isValidCourierCode(item.directOrderCourierCode, this.courierCodeList)
                ) {
                    item.directOrderCourierCode = SharedMsgHelper.getInvalidCourierCodeErrMsg;
                }
                
                return item;
            });
        },
        async onImportClick() {
            this.data.mappedCsv = this.transformMappedCsv(this.data.mappedCsv);
            if (this.data.mappedCsv && this.data.mappedCsv.length > 0) {
                await this.importSubmit();
            } else {
                console.log('empty csv');
            }
        },
        async importSubmit() {
            this.loading = true;
            const isValid = this.validation(this.data.mappedCsv);
            if (isValid) {
                for (const item in this.data.mappedCsv) {
                    await this.update(item);
                }
                this.importDone = true;
                // prevent import again
                this.invalidFile = true;
            } else {
                console.log('invalid csv');
            }
            this.loading = false;
        },
        validation(list) {
            return _.every(list, (v) => {
                return !this.isInvalidRow(v);
            });
        },
        invalidRowClass(item) {
            if (this.isInvalidRow(item)) {
                return 'row-validation-error';
            }
            return '';
        },
        isInvalidRow(item) {
            return (
                this.isInvalidRowContent(item) || item.status === ImportTableLabelTextHelper.getImportResultValue.error
            );
        },
        isInvalidRowContent(item) {
            // Check validation errors from config
            const hasConfigErrors = ImportValidationHelper.hasValidationErrors(item, UpdateLastMileReferenceImportValidation);
            
            // Also check for courier code validation error (dynamic validation)
            const hasCourierCodeError = item.directOrderCourierCode === SharedMsgHelper.getInvalidCourierCodeErrMsg;
            
            return hasConfigErrors || hasCourierCodeError;
        },
        async update(idx) {
            const csvData = this.data.mappedCsv[idx];
            const response = await this.updateCarrierReference(csvData);
            this.processResponse(response, idx, csvData);
        },
        async updateCarrierReference(csvData) {
            const request = _.pick(csvData, ['directOrderNumber', 'directOrderCourierCode', 'carrierReference']);
            const requestUrl = `/api/operation/directOrder/updateCarrierReference`;
            return await fetchWrapper.post(requestUrl, request);
        },
        processResponse(response, idx, csvData) {
            if (response.success) {
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.success;
            } else {
                csvData.status = ImportTableLabelTextHelper.getImportResultValue.error;
                csvData.error = response.errors;
            }
            this.data.mappedCsv.splice(idx, 1, csvData);
        }
    }
});
</script>

<style>
select {
    border: 1px solid !important;
}

.row-validation-error {
    background: red;
}
</style>
